/**
 * Continuous marquee announcement bar.
 * Clones the message group for a seamless loop; duration scales to content width.
 */
class AnnouncementBar extends HTMLElement {
  /** @type {ResizeObserver|undefined} */
  #resizeObserver;

  /** @type {MediaQueryList|undefined} */
  #motionQuery;

  /** @type {HTMLElement|null} */
  #track = null;

  /** @type {HTMLElement|null} */
  #group = null;

  connectedCallback() {
    this.#track = this.querySelector('.announcement-bar__track');
    this.#group = this.querySelector('.announcement-bar__group');

    if (!this.#track || !this.#group) return;

    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#setup);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#resizeObserver = new ResizeObserver(() => this.#setup());
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.#group);

    requestAnimationFrame(() => this.#setup());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#motionQuery?.removeEventListener('change', this.#setup);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  #setup = () => {
    if (!this.#track || !this.#group) return;

    this.#clearClones();

    if (this.#motionQuery?.matches) {
      this.#track.removeAttribute('data-animated');
      this.style.removeProperty('--announcement-shift');
      this.style.removeProperty('--announcement-duration');
      return;
    }

    this.#duplicateGroups();
    this.#updateDuration();
    this.#track.setAttribute('data-animated', 'true');
  };

  #clearClones() {
    this.#track?.querySelectorAll('[data-clone]').forEach((node) => node.remove());
  }

  #duplicateGroups() {
    const track = this.#track;
    const group = this.#group;
    if (!track || !group) return;

    const viewportWidth = this.clientWidth || window.innerWidth;
    const groupWidth = group.getBoundingClientRect().width;
    if (!groupWidth) return;

    let totalWidth = groupWidth;
    const targetWidth = Math.max(viewportWidth * 2, groupWidth * 2);

    while (totalWidth < targetWidth) {
      const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => {
        link.setAttribute('tabindex', '-1');
      });
      track.appendChild(clone);
      totalWidth += groupWidth;
    }
  }

  #updateDuration() {
    const group = this.#group;
    if (!group) return;

    const width = group.getBoundingClientRect().width;
    if (!width) return;

    const speed = Number(this.getAttribute('speed') || 5);
    const pixelsPerSecond = Math.max(24, 110 - speed * 8);
    const duration = width / pixelsPerSecond;

    this.style.setProperty('--announcement-shift', `${width}px`);
    this.style.setProperty('--announcement-duration', `${duration}s`);
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  #handleVisibilityChange = () => {
    this.paused = document.hidden;
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}